import { useState } from "react";

/**
 * Custom hook for managing delete animations
 * Provides a consistent fade-out animation experience across the app
 *
 * @param {number} animationDuration - Duration of the animation in milliseconds (default: 300)
 * @returns {Object} - Object containing deletion state and handlers
 */
export const useDeleteAnimation = (animationDuration = 300) => {
  const [deletingItems, setDeletingItems] = useState(new Set());

  /**
   * Start the deletion animation for an item
   * @param {string|number} itemId - The ID of the item being deleted
   */
  const startDeleteAnimation = (itemId) => {
    setDeletingItems((prev) => new Set(prev).add(itemId));
  };

  /**
   * Complete the deletion animation and remove item from UI
   * @param {string|number} itemId - The ID of the item that was deleted
   * @param {Function} removeFromUI - Function to remove the item from the UI state
   */
  const completeDeleteAnimation = (itemId, removeFromUI) => {
    setTimeout(() => {
      removeFromUI();
      setDeletingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }, animationDuration);
  };

  /**
   * Cancel the deletion animation (e.g., if deletion fails)
   * @param {string|number} itemId - The ID of the item to cancel deletion for
   */
  const cancelDeleteAnimation = (itemId) => {
    setDeletingItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };

  /**
   * Check if an item is currently being deleted
   * @param {string|number} itemId - The ID of the item to check
   * @returns {boolean} - True if the item is being deleted
   */
  const isDeleting = (itemId) => {
    return deletingItems.has(itemId);
  };

  /**
   * Get the current count of items being deleted
   * @returns {number} - Number of items currently being deleted
   */
  const getDeletingCount = () => {
    return deletingItems.size;
  };

  return {
    deletingItems,
    startDeleteAnimation,
    completeDeleteAnimation,
    cancelDeleteAnimation,
    isDeleting,
    getDeletingCount,
  };
};

